import { computed, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { QPointMapMode, QPointMapModeConfiguration, QPointMapModeScope } from './mode';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class MapModeStateService {
  private modes$$ = new BehaviorSubject<QPointMapModeConfiguration[]>([]);
  modesSignal = toSignal(this.modes$$);

  anyModeActive = computed<boolean>(() => {
    return this.modesSignal().some(mode => mode.active);
  });

  get modes() {
    return this.modes$$.getValue();
  }

  getModeConfiguration(mode: QPointMapMode, scope?: QPointMapModeScope): QPointMapModeConfiguration {
    return this.modes.find(c => {
      if (scope && scope != "") {
        return c.mode === mode && c.scope === scope;
      } else {
        return c.mode === mode && (c.scope === 'all' || !c.scope);
      }
    });
  }

  setModes(modes: QPointMapModeConfiguration[]) {
    this.modes$$.next(modes.map(mode => ({
      ...mode,
      scope: mode.scope || 'all'
    })));
  }

  isModeActive(mode: QPointMapMode, scope?: QPointMapModeScope): boolean | undefined {
    return this.getModeConfiguration(mode, scope)?.active ?? undefined;
  }

  isModeActive$(mode: QPointMapMode, scope?: QPointMapModeScope): Observable<boolean | undefined> {
    return this.modes$$.pipe(
      distinctUntilChanged(),
      map(() => {
        return this.isModeActive(mode, scope);
      })
    );
  }

  updateModeActive(mode: QPointMapMode, active: boolean, scope?: QPointMapModeScope): void {
    let modes = cloneDeep(this.modes);
    const config = modes.find(c => {
      if (scope && scope != "") {
        return c.mode === mode && c.scope === scope;
      } else {
        return c.mode === mode && (c.scope === 'all' || !c.scope);
      }
    });

    if (!config.multi) {
      modes = modes.map(c => {
        return {
          ...c,
          active: c === config ? active : false
        };
      });
    } else {
      modes.splice(modes.findIndex(c => {
        if (scope && scope != "") {
          return c.mode === mode && c.scope === scope;
        } else {
          return c.mode === mode && (c.scope === 'all' || !c.scope);
        }
      }), 1, { ...config, active });
    }

    this.setModes(modes);
  }

  deactivateAll() {
    if (this.modes?.length === 0) {
      return;
    }

    this.setModes((this.modes).map(c => {
      return {
        ...c,
        active: false,
      };
    }));
  }

}
